import { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { message } from "antd";
import Utilities from "../../dependencies/custom/react-utilities";
import domainSettings from "../../dependencies/custom/settings";

export const useClassTeachers = () => {
  const navigate = useNavigate();
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchAssignments = useCallback(
    async (params = {}) => {
      setLoading(true);
      try {
        const queryObj = {};
        if (params.school_class) queryObj.school_class = params.school_class;
        if (params.stream) queryObj.stream = params.stream;
        if (params.academic_year) queryObj.academic_year = params.academic_year;
        if (params.term) queryObj.term = params.term;

        const queryParams = new URLSearchParams(queryObj).toString();
        const url = `${domainSettings.urls.academics.classTeachers}${queryParams ? "?" + queryParams : ""}`;
        const response = await Utilities.fetchWithAuth(
          "GET",
          url,
          null,
          navigate,
        );

        if (response.ok) {
          setAssignments(
            Array.isArray(response.data)
              ? response.data
              : response.data?.results || [],
          );
        } else {
          Utilities.showError(
            response,
            "Failed to fetch class teacher assignments",
          );
        }
      } catch (error) {
        message.error("Error fetching class teacher assignments");
        console.error(error);
      } finally {
        setLoading(false);
      }
    },
    [navigate],
  );

  const createAssignment = useCallback(
    async (data) => {
      setLoading(true);
      try {
        const response = await Utilities.fetchWithAuth(
          "POST",
          domainSettings.urls.academics.classTeachers,
          data,
          navigate,
        );
        if (response.ok) {
          message.success("Class teacher assigned successfully");
          return response.data;
        }
        Utilities.showError(response, "Failed to assign class teacher");
        return null;
      } catch (error) {
        message.error("Error assigning class teacher");
        console.error(error);
        return null;
      } finally {
        setLoading(false);
      }
    },
    [navigate],
  );

  const updateAssignment = useCallback(
    async (id, data) => {
      setLoading(true);
      try {
        const url = domainSettings.urls.academics.classTeacherDetail.replace(
          ":id",
          id,
        );
        const response = await Utilities.fetchWithAuth(
          "PATCH",
          url,
          data,
          navigate,
        );
        if (response.ok) {
          message.success("Assignment updated successfully");
          return response.data;
        }
        Utilities.showError(response, "Failed to update assignment");
        return null;
      } catch (error) {
        message.error("Error updating assignment");
        console.error(error);
        return null;
      } finally {
        setLoading(false);
      }
    },
    [navigate],
  );

  const deleteAssignment = useCallback(
    async (id) => {
      try {
        const url = domainSettings.urls.academics.classTeacherDetail.replace(
          ":id",
          id,
        );
        const response = await Utilities.fetchWithAuth(
          "DELETE",
          url,
          null,
          navigate,
        );
        if (response.ok) {
          message.success("Assignment removed successfully");
          return true;
        }
        Utilities.showError(response, "Failed to remove assignment");
        return false;
      } catch (error) {
        message.error("Error removing assignment");
        console.error(error);
        return false;
      }
    },
    [navigate],
  );

  const swapTeachers = useCallback(
    async (id, targetID) => {
      try {
        const url = domainSettings.urls.academics.classTeacherSwap.replace(
          ":id",
          id,
        );
        const response = await Utilities.fetchWithAuth(
          "POST",
          url,
          { targetID },
          navigate,
        );
        if (response.ok) {
          message.success(
            response.data?.detail || "Teachers swapped successfully",
          );
          return true;
        }
        Utilities.showError(response, "Failed to swap teachers");
        return false;
      } catch (error) {
        message.error("Error swapping teachers");
        console.error(error);
        return false;
      }
    },
    [navigate],
  );

  return {
    assignments,
    loading,
    fetchAssignments,
    createAssignment,
    updateAssignment,
    deleteAssignment,
    swapTeachers,
  };
};

export default useClassTeachers;
